// Thin fetch wrapper for the Worker API.

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

let onUnauthorized = () => {};
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

let guestFingerprint = "";
/** Sent with guest requests so private windows of the same browser share a quota. */
export function setGuestFingerprint(fp) {
  guestFingerprint = fp;
}

let onWorkspaceExpired = () => {};
/** Called when the server says this tab's workspace was deleted (idle too long). */
export function setWorkspaceExpiredHandler(fn) {
  onWorkspaceExpired = fn;
}
const isExpired = (status, message) => status === 404 && /workspace has expired/.test(message || "");

async function parseError(resp) {
  try {
    const body = await resp.json();
    return body.error || body.message || `Request failed (${resp.status})`;
  } catch {
    return `Request failed (${resp.status})`;
  }
}

export async function request(method, path, { json, body, headers = {}, raw = false, signal } = {}) {
  const init = { method, headers: { "X-Requested-With": "ABVME", ...headers }, credentials: "same-origin", signal };
  if (guestFingerprint && path.startsWith("/api/guest/")) init.headers["X-ABVME-FP"] = guestFingerprint;
  if (json !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  } else if (body !== undefined) {
    init.body = body;
  }
  let resp;
  try {
    resp = await fetch(path, init);
  } catch (err) {
    if (err.name === "AbortError") throw err;
    throw new ApiError(0, "Network error — check your connection");
  }
  if (!resp.ok) {
    const message = await parseError(resp);
    if (resp.status === 401 && !path.startsWith("/api/auth/")) onUnauthorized(message);
    if (isExpired(resp.status, message)) onWorkspaceExpired(message);
    throw new ApiError(resp.status, message);
  }
  if (raw) return resp;
  const type = resp.headers.get("content-type") || "";
  return type.includes("application/json") ? resp.json() : resp.text();
}

export const api = {
  get: (path, opts) => request("GET", path, opts),
  post: (path, json, opts = {}) => request("POST", path, { json, ...opts }),
  put: (path, json, opts = {}) => request("PUT", path, { json, ...opts }),
  patch: (path, json, opts = {}) => request("PATCH", path, { json, ...opts }),
  del: (path, opts) => request("DELETE", path, opts),
};

/**
 * XHR upload with progress (fetch has no upload progress events). Resolves with
 * the JSON response, or with {data: ArrayBuffer, header(name)} when `binary`.
 */
export function xhrUpload(method, url, blob, { headers = {}, onProgress, signal, binary = false } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = true;
    if (binary) xhr.responseType = "arraybuffer";
    xhr.setRequestHeader("X-Requested-With", "ABVME");
    if (guestFingerprint && url.startsWith("/api/guest/")) xhr.setRequestHeader("X-ABVME-FP", guestFingerprint);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded, e.total);
    xhr.onload = () => {
      const ok = xhr.status >= 200 && xhr.status < 300;
      if (ok && binary) {
        resolve({ data: xhr.response, header: (name) => xhr.getResponseHeader(name) });
        return;
      }
      let data = null;
      try {
        data = JSON.parse(binary ? new TextDecoder().decode(xhr.response) : xhr.responseText);
      } catch {
        data = null;
      }
      if (ok) resolve(data);
      else {
        const message = data?.error || data?.message || `Upload failed (${xhr.status})`;
        if (xhr.status === 401) onUnauthorized(data?.error);
        if (isExpired(xhr.status, message)) onWorkspaceExpired(message);
        reject(new ApiError(xhr.status, message));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error during upload"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(blob);
  });
}

/** Trigger a browser download for a same-origin URL. */
export function downloadUrl(url, filename = "") {
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  downloadUrl(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
